import { inject, provide, type Component, type InjectionKey } from 'vue';

/**
 * A link can be a component (NuxtLink, RouterLink) or a plain tag name.
 */
export type LinkComponent = Component | string;

/**
 * Injection key for the host app's link component.
 *
 * - `app` (Nuxt) provides `NuxtLink`
 * - `admin` (vue-router) provides `RouterLink`
 *
 * Shared components read it via {@link useLink} and fall back to a plain
 * anchor when no host has provided one.
 */
export const LINK_INJECTION_KEY: InjectionKey<LinkComponent> =
  Symbol('exfluential-link');

/**
 * Register the link component for shared components to use.
 *
 * Call from a `setup` context. For app-level registration (Nuxt plugin or
 * `main.ts`) provide {@link LINK_INJECTION_KEY} directly on the Vue app
 * instance instead, e.g. `vueApp.provide(LINK_INJECTION_KEY, NuxtLink)`.
 */
export const provideLink = (component: LinkComponent): void => {
  provide(LINK_INJECTION_KEY, component);
};

/**
 * Resolve the host app's link component, defaulting to a plain `<a>`.
 */
export const useLink = (): LinkComponent => {
  return inject(LINK_INJECTION_KEY, 'a');
};
